import { useState } from "react";
import { Button as AntButton, Tag, Typography } from "antd";
import { CheckCircleFilled, CloseCircleFilled, CloseOutlined } from "@ant-design/icons";
import { useTheme } from "../../../contexts/ThemeContext";
import { plural } from "./additionalInfoUtils";

const { Text } = Typography;
const VISIBLE = 5;

/**
 * Resultado do último envio, inline: uma linha de resumo e, se houver, a lista compacta de
 * erros e avisos (aba · linha · coluna). Com erro nada foi importado.
 */
const UploadResult = ({ result, supportsSales, onClose }) => {
  const { theme } = useTheme();
  const [expanded, setExpanded] = useState(false);
  if (!result) return null;

  const failed = result.erros.length > 0;
  const messages = [
    ...result.erros.map((m) => ({ ...m, kind: "erro" })),
    ...result.avisos.map((m) => ({ ...m, kind: "aviso" })),
  ];
  const visible = expanded ? messages : messages.slice(0, VISIBLE);

  let summary;
  if (failed) {
    summary = `Nada foi importado — ${plural(result.erros.length, "erro", "erros")} para corrigir`;
  } else {
    const parts = [plural(result.qtdTitulos ?? 0, "título", "títulos")];
    if (supportsSales) parts.push(plural(result.qtdVendas ?? 0, "venda", "vendas"));
    // O aviso com o que foi substituído do envio anterior vem em avisos[0], vindo do Backend.
    summary = `Importado: ${parts.join(" e ")}`;
  }

  return (
    <div style={{ border: `1px solid ${theme.border}`, borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {failed ? (
          <CloseCircleFilled style={{ color: theme.error }} />
        ) : (
          <CheckCircleFilled style={{ color: theme.success }} />
        )}
        <Text strong style={{ flex: 1, fontSize: 13, color: theme.textPrimary }}>
          {summary}
        </Text>
        <AntButton type="text" size="small" icon={<CloseOutlined />} onClick={onClose} aria-label="Fechar resultado" />
      </div>

      {!failed && result.cargaIniciada === false && (
        <Text style={{ display: "block", marginTop: 4, fontSize: 12, color: theme.textSecondary }}>
          A carga incremental não foi iniciada agora; os lançamentos entram na próxima sincronização.
        </Text>
      )}

      {messages.length > 0 && (
        <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
          {visible.map((m, index) => {
            const where = [m.aba, m.linha ? `linha ${m.linha}` : null, m.coluna].filter(Boolean).join(" · ");
            return (
              <li key={`${m.kind}-${index}`} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <Tag
                  bordered={false}
                  color={m.kind === "erro" ? "error" : "warning"}
                  style={{ margin: 0, fontSize: 11, lineHeight: "18px", flex: "0 0 auto" }}
                >
                  {m.kind === "erro" ? "Erro" : "Aviso"}
                </Tag>
                <div style={{ minWidth: 0 }}>
                  {where && <Text style={{ fontSize: 12, color: theme.textSecondary }}>{`${where} — `}</Text>}
                  <Text style={{ fontSize: 13, color: theme.textPrimary }}>{m.mensagem}</Text>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {messages.length > VISIBLE && (
        <AntButton type="link" size="small" style={{ padding: 0, marginTop: 6 }} onClick={() => setExpanded((e) => !e)}>
          {expanded ? "Mostrar menos" : `Ver todos (${messages.length})`}
        </AntButton>
      )}
    </div>
  );
};

export default UploadResult;
